import Link from "next/link";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from "@reach/accordion";
import { Icon } from "@nju/icons";
import { A, BlockText, Heading, Separator, Text } from "@nju/ui";
import { LoginForm } from "./LoginForm";

export function LoginPage() {
  return (
    <div className="py-6 from-tablet:py-[90px]">
      <Heading
        className="text-size-32"
        font="latoBlack"
        tone="neutral"
        variant="h1"
        weight="bold"
      >
        Zaloguj się
      </Heading>
      <div className="flex flex-col from-desktop:flex-row gap-8 mt-4 from-tablet:mt-8">
        <div className="flex-1" data-cy="login-form-box">
          <LoginForm />
        </div>
        <div className="flex-1 flex flex-col gap-8">
          <div
            className="bg-porcelain rounded-[15px] px-3 py-4 from-tablet:p-8"
            data-cy="two-accounts-info-box"
          >
            <Heading
              font="latoBlack"
              tone="neutral"
              variant="dashboardTitle"
              weight="bold"
            >
              Dwa konta w nju
            </Heading>
            <BlockText className="my-6" size="l">
              W nju zarządzanie usługami jest proste - masz dwa oddzielne konta
              dla różnych typów usług. Jedno dla usług abonamentowych, na kartę
              i internetu mobilnego, a drugie dla subskrypcji i światłowodu.
            </BlockText>
            <Accordion collapsible={true}>
              <AccordionItem>
                <AccordionButton
                  className="bg-transparent border-none flex items-center cursor-pointer [&[aria-expanded='true']_svg]:rotate-0 w-full"
                  data-cy="two-accounts-info-box-first-paragraph-button"
                >
                  <div className="flex items-center justify-between w-full gap-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <Icon
                          height={24}
                          name="user-circle-thick"
                          tone="neutral"
                          width={24}
                        />
                      </div>
                      <Text size="l" weight="bold">
                        abonament, na kartę, internet mobilny
                      </Text>
                    </div>
                    <Icon
                      className="shrink-0 rotate-180"
                      height={14}
                      name="chevron-up-thick"
                      tone="neutral"
                      width={21}
                    />
                  </div>
                </AccordionButton>
                <AccordionPanel className="mt-4">
                  <BlockText size="m">
                    Jeśli chcesz zarządzać usługami{" "}
                    <Text size="m" weight="bold">
                      abonamentu, na kartę, internetu mobilnego
                    </Text>
                    , zaloguj się do tego konta. Zarządzanie usługami odbywa się
                    przez stronę WWW.
                  </BlockText>
                  <BlockText className="mt-4" size="m">
                    Zalogować sie możesz wykorzystując:
                  </BlockText>
                  <div className="flex items-center gap-3 mt-3">
                    <Icon
                      height={24}
                      name="phone-thick"
                      tone="accent"
                      width={16}
                    />
                    <Text size="m">numer telefonu</Text>
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            <Separator size="m" />
            <Accordion
              collapsible={true}
              data-cy="two-accounts-info-box-second-paragraph"
            >
              <AccordionItem>
                <AccordionButton
                  className="bg-transparent border-none flex items-center cursor-pointer [&[aria-expanded='true']_svg]:rotate-0 w-full"
                  data-cy="two-accounts-info-box-second-paragraph-button"
                >
                  <div className="flex items-center justify-between w-full gap-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <Icon
                          height={24}
                          name="user-circle-thick"
                          tone="neutral"
                          width={24}
                        />
                      </div>
                      <Text size="l" weight="bold">
                        subskrypcja, światłowód
                      </Text>
                    </div>
                    <Icon
                      className="shrink-0 rotate-180"
                      height={14}
                      name="chevron-up-thick"
                      tone="neutral"
                      width={21}
                    />
                  </div>
                </AccordionButton>
                <AccordionPanel className="mt-4">
                  <BlockText size="m">
                    Jeśli chcesz zarządzać usługami{" "}
                    <Text size="m" weight="bold">
                      subskrypcji, światłowodu
                    </Text>
                    , zaloguj się do tego konta. Zarządzanie usługami odbywa się
                    przez stronę WWW lub aplikację mobilną.
                  </BlockText>
                  <BlockText className="mt-4" size="m">
                    Zalogować sie możesz wykorzystując:
                  </BlockText>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3">
                    <div className="flex items-center gap-x-4">
                      <div className="flex items-center gap-3">
                        <Icon
                          height={18}
                          name="mail-thick"
                          tone="accent"
                          width={24}
                        />
                        <Text size="m">adres e-mail</Text>
                      </div>
                      <Text size="m">lub</Text>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon
                        className="font-bold"
                        height={24}
                        name="globe-thick"
                        tone="accent"
                        width={24}
                      />
                      <Text size="m">konto Google / Facebook</Text>
                    </div>
                  </div>
                  <BlockText className="mt-8" size="m" weight="bold">
                    Wygodny sposób zarządzania usługami subskrypcji i
                    światłowodu w telefonie
                  </BlockText>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
                    <div className="flex items-center gap-3">
                      <Icon
                        height={21}
                        name="cloud-thick"
                        tone="accent"
                        width={30}
                      />
                      <Text size="m">Pobierz aplikację na telefon</Text>
                    </div>
                    <div className="flex gap-2">
                      <Link
                        data-cy="app-store-download-app-button"
                        href="https://app.appsflyer.com/1578850119"
                        title="Pobierz w App Store"
                      >
                        <img
                          alt="Pobierz w App Store"
                          className="w-[116px] h-[39px]"
                          src="/images/download-app-store-black.svg"
                        />
                      </Link>
                      <Link
                        data-cy="google-play-download-app-button"
                        href="https://app.appsflyer.com/com.orange.rn.nju"
                        title="Pobierz z Google Play"
                      >
                        <img
                          alt="Pobierz z Google Play"
                          className="w-[132px] h-[39px]"
                          src="/images/download-google-play.svg"
                        />
                      </Link>
                    </div>
                  </div>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </div>
          <div
            className="bg-ripeLemon rounded-[15px] py-4 px-3 from-tablet:p-8"
            data-cy="create-account-box"
          >
            <Heading
              font="latoBlack"
              tone="neutral"
              variant="dashboardTitle"
              weight="bold"
            >
              Utwórz konto
            </Heading>
            <BlockText
              className="mb-6 mt-3 from-tablet:my-6"
              size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
            >
              Nie czekaj dłużej i załóż konto nju już dziś!
            </BlockText>
            <Link
              className="block from-tablet:w-[160px]"
              data-cy="create-account-button"
              href="https://www.njumobile.pl/zaloz-konto"
              passHref={true}
            >
              <A size="register" variant="primaryWithBorder">
                załóż konto
              </A>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
